import { useState, useRef, useEffect, useCallback } from 'react';

import { Check } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

import { FlagIcon, type FlagCode } from '../../primitives/FlagIcon';
import { IconButton } from '../../primitives/IconButton';
import { Tooltip } from '../../primitives/Tooltip';
import { cn } from '../../utils';

export interface LanguageOption {
  /** Language code (e.g., 'es', 'en', 'zh') */
  code: string;
  /** Display label (e.g., 'Español', 'English', '中文') */
  label: string;
  /** Country code for flag (e.g., 'MX', 'US', 'CN') */
  countryCode: FlagCode;
}

export interface LanguageSelectorProps {
  /** Available languages */
  languages: LanguageOption[];
  /** Currently selected language code */
  currentLanguage: string;
  /** Callback when language changes */
  onLanguageChange: (languageCode: string) => void;
  /** Tooltip text */
  tooltipText?: string;
  /** Additional class name */
  className?: string;
}

/**
 * LanguageSelector - Dropdown selector for changing app language
 *
 * Features:
 * - Displays current language flag
 * - Animated popover with language options
 * - Keyboard accessible
 * - Click outside to close
 */
export function LanguageSelector({
  languages,
  currentLanguage,
  onLanguageChange,
  tooltipText = 'Cambiar idioma',
  className,
}: LanguageSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[0];

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleSelect = useCallback(
    (code: string) => {
      onLanguageChange(code);
      setIsOpen(false);
    },
    [onLanguageChange]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, code: string) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleSelect(code);
      }
    },
    [handleSelect]
  );

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <Tooltip content={tooltipText} position="bottom">
        <IconButton
          variant="ghost"
          size="md"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={tooltipText}
          className={cn(isOpen && 'bg-tkf-surface-muted')}
        >
          <FlagIcon code={currentLang?.countryCode || 'MX'} size="md" />
        </IconButton>
      </Tooltip>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={menuRef}
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
            className={cn(
              'absolute right-0 top-full mt-2 z-dropdown',
              'min-w-[160px] rounded-lg border border-tkf-line-light bg-tkf-surface shadow-xl',
              'overflow-hidden'
            )}
            role="listbox"
            aria-label="Seleccionar idioma"
          >
            <div className="py-1">
              {languages.map(lang => {
                const isSelected = lang.code === currentLanguage;
                return (
                  <button
                    key={lang.code}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(lang.code)}
                    onKeyDown={e => handleKeyDown(e, lang.code)}
                    className={cn(
                      'flex w-full items-center gap-3 px-3 py-2',
                      'text-sm text-tkf-fg',
                      'transition-colors',
                      isSelected ? 'bg-primary-50 text-primary-700' : 'hover:bg-tkf-surface-subtle'
                    )}
                  >
                    <FlagIcon code={lang.countryCode} size="md" />
                    <span className="flex-1 text-left">{lang.label}</span>
                    {isSelected && <Check className="h-4 w-4 text-primary-600" />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
